import { Dispatch, SetStateAction, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Filter } from 'components/molecules/filters/types';
import { Just, Nothing, isEmpty } from 'core/maybe';
import { useLocalStorage } from 'hooks/use-local-storage';
import { logger } from 'services';
import { TaskField } from 'stores/fields';
import { from_search, to_query } from '../filter-transformer';

interface Props {
  localStorageKey: string;
  taskStatus: string;
  replaceFilter: (filter: Filter) => void;
  filter: Filter;
  fieldsLoading: boolean;
  fieldLookup: Record<string, TaskField>;
  filterInitialized: boolean;
  setFilterInitialized: Dispatch<SetStateAction<boolean>>;
}
export const useProjectTaskListFilter = ({
  localStorageKey,
  taskStatus,
  replaceFilter,
  filter,
  fieldsLoading,
  filterInitialized,
  setFilterInitialized,
  fieldLookup,
}: Props): void => {
  const [localStorageValue, setLocalStorageValue] = useLocalStorage(
    localStorageKey,
    '',
    {
      deserialize: (value) => {
        if (isEmpty(value)) {
          return value;
        }
        try {
          const parsedValue = JSON.parse(JSON.stringify(value[1]));

          return Just(parsedValue);
        } catch {
          logger.info(
            'Deserializing of filter query from local storage failed for local storage key:',
            localStorageKey,
          );

          return Nothing();
        }
      },
      serialize: (value) => String(value),
    },
  );
  const { search } = useLocation();
  const { replace } = useHistory();

  useEffect(() => {
    if (!filterInitialized || taskStatus === 'loading') {
      return;
    }
    setLocalStorageValue(to_query(filter));
  }, [filter, filterInitialized, setLocalStorageValue, taskStatus]);

  useEffect(() => {
    if (filterInitialized || fieldsLoading) {
      return;
    }
    const restore_query = (search ?? '') === '' ? localStorageValue : search;
    const saved_filter = from_search(fieldLookup)(restore_query);

    replaceFilter(saved_filter);
    setFilterInitialized(true);
  }, [
    filterInitialized,
    setFilterInitialized,
    fieldsLoading,
    fieldLookup,
    search,
    localStorageValue,
    replace,
    replaceFilter,
  ]);
};
